import { useState } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import {
  Menu,
  X,
  Home,
  FileText,
  FolderOpen,
  Image,
  Link2,
  LogOut,
  Terminal,
} from "lucide-react";

const navItems = [
  {
    title: "Dashboard",
    icon: Home,
    path: "/admin-5390214552274/dashboard",
  },
  {
    title: "Accounts",
    icon: FileText,
    path: "/admin-5390214552274/dashboard/accounts",
  },
  {
    title: "Documents",
    icon: FolderOpen,
    path: "/admin-5390214552274/dashboard/documents",
  },
  {
    title: "Gallery",
    icon: Image,
    path: "/admin-5390214552274/dashboard/gallery",
  },
  {
    title: "Pages",
    icon: Link2,
    path: "/admin-5390214552274/dashboard/pages",
  },
];

export const AdminLayout = ({ children, title }) => {
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const { logout, user } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();

  const handleLogout = async () => {
    await logout();
    navigate("/admin-5390214552274");
  };

  const handleNavClick = (path) => {
    navigate(path);
    setSidebarOpen(false);
  };

  return (
    <div className="admin-layout">
      {/* Mobile overlay */}
      {sidebarOpen && (
        <div
          className="sidebar-overlay md:hidden"
          onClick={() => setSidebarOpen(false)}
        />
      )}

      {/* Sidebar */}
      <aside
        className={`admin-sidebar ${sidebarOpen ? "open" : ""}`}
        data-testid="admin-sidebar"
      >
        {/* Logo */}
        <div className="p-4 border-b border-[#15803d]">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <Terminal className="text-[#22c55e]" size={24} />
              <span className="text-[#22c55e] font-['VT323'] text-xl text-glow">
                KMB.LOL
              </span>
            </div>
            <button
              onClick={() => setSidebarOpen(false)}
              className="md:hidden text-[#22c55e]"
            >
              <X size={24} />
            </button>
          </div>
        </div>

        {/* Navigation */}
        <nav className="flex-1 p-4 space-y-2">
          {navItems.map((item) => {
            const isActive = location.pathname === item.path;
            return (
              <button
                key={item.path}
                onClick={() => handleNavClick(item.path)}
                className={`w-full flex items-center gap-3 px-4 py-3 text-left transition-colors ${
                  isActive
                    ? "bg-[#22c55e] text-[#050505]"
                    : "text-[#4ade80] hover:bg-[#15803d]/20 hover:text-[#22c55e]"
                }`}
                data-testid={`nav-${item.title.toLowerCase()}`}
              >
                <item.icon size={20} />
                <span className="font-mono text-sm uppercase tracking-wider">
                  {item.title}
                </span>
              </button>
            );
          })}
        </nav>

        {/* User section */}
        <div className="p-4 border-t border-[#15803d]">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-[#06b6d4] text-xs uppercase tracking-wider">
                Logged in as
              </p>
              <p className="text-[#22c55e] font-mono">{user || "admin"}</p>
            </div>
            <button
              onClick={handleLogout}
              className="p-2 text-[#ef4444] hover:bg-[#ef4444]/10 transition-colors"
              title="Logout"
              data-testid="logout-btn"
            >
              <LogOut size={20} />
            </button>
          </div>
        </div>
      </aside>

      {/* Main content */}
      <main className="admin-content">
        {/* Top bar */}
        <header className="sticky top-0 z-40 bg-[#0a0a0a] border-b border-[#15803d] px-4 py-3">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <button
                onClick={() => setSidebarOpen(true)}
                className="md:hidden text-[#22c55e]"
                data-testid="menu-toggle"
              >
                <Menu size={24} />
              </button>
              <h1 className="text-[#22c55e] font-['VT323'] text-xl md:text-2xl text-glow">
                [ {title || "ADMIN"} ]
              </h1>
            </div>
            <div className="flex items-center gap-4">
              <span className="text-[#06b6d4] text-sm font-mono hidden md:block">
                {new Date().toLocaleString()}
              </span>
              <button
                onClick={handleLogout}
                className="flex items-center gap-2 px-3 py-1.5 border border-[#ef4444] text-[#ef4444] hover:bg-[#ef4444] hover:text-[#050505] transition-colors text-sm"
                title="Logout"
                data-testid="topbar-logout-btn"
              >
                <LogOut size={16} />
                <span className="hidden sm:inline">Logout</span>
              </button>
            </div>
          </div>
        </header>

        {/* Page content */}
        <div className="min-h-[calc(100vh-60px)]">{children}</div>
      </main>
    </div>
  );
};

export default AdminLayout;
